import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { PageOrder } from '../components/page/pageOrder';
import { AppContext } from '../containers/appContextContainer';
import {
    EiendomsverdiContainerWeb,
    EiendomsverdiUpdateWeb,
    EiendomTypeWeb,
    EiendomWeb,
    SoeknadstypeWeb,
    VerdivurderingInfoWeb,
} from '../generated-types';
import { createActionVerdivurdering } from '../util/gtm/customDataLayer-service';
import errorTypes from '../util/gtm/errorTypes';
import { pushErrorToDataLayer } from '../util/gtm/gtm-service';
import restClient from '../util/web-http-client';

export const useEiendomregister = () => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['eiendomregister'],
        queryFn: () => restClient.getEiendomsregister(),
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_EIENDOMSREGISTER,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useEiendomverdi = (soeknadtype: SoeknadstypeWeb, eiendomType: EiendomTypeWeb) => {
    const { setError } = useContext(AppContext);
    const { setPageOrder } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['eiendomverdi'],
        queryFn: () =>
            restClient.getEiendomsverdi$GET$eiendom_soeknadType_eiendomType_eiendomsverdi(soeknadtype, eiendomType),
        retry: 1,
        refetchOnMount: 'always',
    });

    useEffect(() => {
        if (query.data) {
            if (query.data.soeknadstype === SoeknadstypeWeb.NYTT_BOLIGLAAN || query.data.annenBolig) {
                setPageOrder(PageOrder(SoeknadstypeWeb.NYTT_BOLIGLAAN));
            }
        }
        if (query.error) {
            setError({
                errorType: errorTypes.GET_EIENDOMSVERDI,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.data, query.error, setPageOrder, setError]);

    return query;
};

export const useUpdateEiendomsverdi = (eiendomsverdi?: EiendomsverdiContainerWeb, successCallback?: () => void) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['updateEiendomsverdi'],
        mutationFn: (eiendomsverdiUpdate: EiendomsverdiUpdateWeb) =>
            restClient.updateEiendomsverdi(eiendomsverdiUpdate),
        onSuccess: (data) => {
            if (eiendomsverdi) {
                eiendomsverdi.harEtakst = data.harEtakst;
                eiendomsverdi.harBolig = data.harBolig;
                eiendomsverdi.harPantIBolig = data.harPantIBolig;
                eiendomsverdi.eksisterendeLaanebeloep = data.eksisterendeLaanebeloep;
                eiendomsverdi.soektBeloep = data.soektBeloep;
                eiendomsverdi.eiendom = data.eiendom;
                eiendomsverdi.laaneformaal = data.laaneformaal;
                eiendomsverdi.annetLaaneformaal = data.annetLaaneformaal;
            }
            queryClient.setQueryData(['eiendomverdi'], eiendomsverdi);
            if (successCallback) {
                successCallback();
            }
        },
        onError: (error) => {
            setError({
                errorType: errorTypes.UPDATE_EINDOMSVERDI,
                errorException: error as RequestExceptionWithResponse,
            });
        },
    });
};

export const useFindEiendomsverdiMatrikkel = (
    soeknadId: string,
    soeknadType: SoeknadstypeWeb,
    eiendom: EiendomWeb | undefined,
) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['eiendomverdiMatrikkel', soeknadId],
        queryFn: () =>
            restClient.findEiendomsverdiMatrikkel(soeknadId, soeknadType, {
                matrikkelWeb: eiendom?.matrikkelWeb || null,
                borettslagAndelWeb: eiendom?.borettslagAndelWeb || null,
            }),
        retry: 1,
        refetchOnWindowFocus: false,
        enabled: eiendom !== undefined,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.FIND_EIENDOMSVERDI,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useDeleteEiendom = (soeknadId: string) => {
    const { setError } = useContext(AppContext);
    return useMutation({
        mutationKey: ['deleteEiendom'],
        mutationFn: (id: string) => restClient.deleteEiendom(soeknadId, id),
        onError: (error) => {
            setError({ errorType: errorTypes.DELETE_EIENDOM, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useETakst = (referansenummer: string) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['eTakst', referansenummer],
        queryFn: () => restClient.getETakst(referansenummer),
        enabled: referansenummer?.length === 5,
    });

    useEffect(() => {
        if (query.error) {
            setError({ errorType: errorTypes.GET_ETAKST, errorException: query.error as RequestExceptionWithResponse });
        }
    }, [query.error, setError]);

    return query;
};

export const usePoststed = (postnummer: string | null | undefined) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['poststed', postnummer],
        queryFn: () => restClient.getPoststed(postnummer || ''),
        enabled: postnummer !== undefined && postnummer !== null && postnummer.length === 4,
        retry: 1,
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_POSTSTED,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useGetAdresser = (query: string) => {
    const { setError } = useContext(AppContext);
    const queryResult = useQuery({
        queryKey: ['adresser', query],
        queryFn: () => restClient.getAdresser({ query }),
        enabled: query !== undefined && query !== null && query.length > 3,
        retry: 1,
    });

    useEffect(() => {
        if (queryResult.error) {
            setError({
                errorType: errorTypes.GET_ADRESSER,
                errorException: queryResult.error as RequestExceptionWithResponse,
            });
        }
    }, [queryResult.error, setError]);

    return queryResult;
};

export const useGetAdresseInfo = (key: string) => {
    return useQuery({
        queryKey: ['key', key],
        queryFn: () => restClient.getAdresseInfo(key),
        enabled: key !== undefined && key !== null && key !== '',
        retry: 1,
    });
};

export const useGetFellesgjeld = (
    andelsnummer: string | null | undefined,
    organisasjonsummer: string | null | undefined,
) => {
    return useQuery({
        queryKey: ['andelFellesgjeld', andelsnummer],
        queryFn: () => restClient.getFellesgjeld(organisasjonsummer || '', andelsnummer || ''),
        enabled: !(!andelsnummer || !organisasjonsummer),
        retry: 1,
    });
};

export const useForespoerVerdivurdering = () => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['verdivurdering'],
        mutationFn: (info: VerdivurderingInfoWeb) => restClient.requestVerdivurdering(info),
        onSuccess: () => {
            createActionVerdivurdering();
            queryClient.invalidateQueries({ queryKey: ['kvitttering'] });
        },
        onError: (error) => {
            pushErrorToDataLayer(errorTypes.BESTILL_VERDIVURDEING, error as RequestExceptionWithResponse);
            setError({
                errorType: errorTypes.REQUEST_VERDIVURDERING,
                errorException: error as RequestExceptionWithResponse,
            });
        },
    });
};

export default useEiendomregister;
